/**
 * Landing in the conversation instead of on an empty dashboard.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT THIS FIXES
 *
 * Sign-up used to end on `/app/dashboard` in manual mode: forty working screens
 * with nothing in them, and no indication of what to do next. The product's
 * whole argument is that you can talk to it — and the first screen hid that
 * behind a rail nobody opens on their first minute.
 *
 * So a new workspace opens in AGENT mode, on the thread the server already
 * seeded with a greeting (backend/src/agent/setup.ts). The customer arrives to
 * a question rather than to an empty table.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE ANSWER COMES FROM THE SERVER, NOT FROM THIS BROWSER
 *
 * "Is this workspace still being set up" is a fact about the workspace, so it is
 * read from `/agent/setup`. Somebody who signs up on a laptop and opens the
 * workspace on a desktop is still mid-setup on both; a flag in localStorage
 * would have known about only one of them.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ONCE, AND THEN NEVER AGAIN
 *
 * The handoff happens at most one time per workspace, recorded locally. That
 * local record is doing the opposite job to the server's answer, and both are
 * needed:
 *
 *   the server  says whether setup is still unanswered
 *   the browser says whether THIS person has already been sent there once
 *
 * Without the second, somebody who reads the greeting, decides they would
 * rather look around the dashboard, and switches to manual gets dragged back
 * into the conversation on their next navigation — over and over, until they
 * answer. That is not onboarding, it is a hostage situation. One nudge is
 * help; a nudge that repeats is the product refusing to be used.
 */

import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useAppMode } from '@/contexts/AgentModeContext';
import { writeThread } from './useAgentChat';

type SetupResponse = { open: boolean; conversation_id: string | null };

const DONE_KEY = 'railit_setup_handoff';

function alreadyHandedOff(orgId: string): boolean {
  try {
    const raw = localStorage.getItem(DONE_KEY);
    return raw ? (JSON.parse(raw) as string[]).includes(orgId) : false;
  } catch {
    return false;
  }
}

function markHandedOff(orgId: string): void {
  try {
    const raw = localStorage.getItem(DONE_KEY);
    const list = raw ? (JSON.parse(raw) as string[]) : [];
    if (!list.includes(orgId)) list.push(orgId);
    localStorage.setItem(DONE_KEY, JSON.stringify(list.slice(-20)));
  } catch {
    /* private browsing: the nudge may repeat once, which is survivable */
  }
}

export function useSetupHandoff(): void {
  const { org, isAuthenticated } = useAuth();
  const { setMode } = useAppMode();

  const { data } = useQuery<SetupResponse>({
    queryKey: ['agent-setup', org?.id],
    queryFn: () => api<SetupResponse>('/agent/setup'),
    /* Asked once per workspace per session. Nothing about the answer changes
       while the page is open except by the customer replying, and that path
       sets the mode itself. */
    enabled: Boolean(isAuthenticated && org?.id) && !alreadyHandedOff(org!.id),
    staleTime: Infinity,
    retry: false,
  });

  useEffect(() => {
    const orgId = org?.id;
    if (!orgId || !data?.open || !data.conversation_id) return;
    if (alreadyHandedOff(orgId)) return;

    /* The pointer first, then the mode — the workspace reads the pointer as it
       mounts, and flipping the mode first would mount it on an empty thread. */
    writeThread(orgId, data.conversation_id);
    markHandedOff(orgId);
    setMode('agent');
  }, [org?.id, data, setMode]);
}
